// frontend/src/api/venues.ts
//
// Partner bars' own contests (BAR-CONTESTS-SCOPE-2026-10-06.md). Both calls
// work logged out: the bar page is the QR landing.

import { API_BASE_URL } from '@/config/apiConfig';
import { ApiError } from '@/utils/appError';
import { createApiClient, isAxiosError } from './client';

const venuesApiClient = createApiClient({ baseURL: API_BASE_URL, timeout: 8000 });

export type VenuePhase = 'entry' | 'live' | 'final' | 'none';

export interface VenueContestResponse {
  venue: { slug: string; name: string; prize_text: string; requires_code: boolean };
  phase: VenuePhase;
  contest_id: number | null;
  /** The latest result, shown above next weekend's builder until its first kickoff. */
  previous_contest_id: number | null;
  next_start_time: string | null;
}

export const fetchVenueContest = async (slug: string): Promise<VenueContestResponse> => {
  try {
    const response = await venuesApiClient.get<VenueContestResponse>(`/dfs/venues/${encodeURIComponent(slug)}`);
    return response.data;
  } catch (error: unknown) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    throw new ApiError(`Failed to load the contest for ${slug}.`, status, { cause: error });
  }
};

/** Resolves when the code is right; a wrong code is the user's to fix. */
export const checkVenueCode = async (slug: string, code: string): Promise<void> => {
  try {
    await venuesApiClient.post(`/dfs/venues/${encodeURIComponent(slug)}/check-code`, { code });
  } catch (error: unknown) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
    if (status === 400 && typeof detail === 'string') {
      throw ApiError.userActionable(detail, status, { cause: error });
    }
    throw new ApiError(`Failed to check the code for ${slug}.`, status, { cause: error });
  }
};
